import React from 'react';
import { usePortalContext } from '../context/PortalContext';
import AdminTenantSelector, { type AdminTenantOption } from '../components/AdminTenantSelector';
import { hasElevatedAccess, PORTAL_ROLES } from '../utils/roles';

type SplashContent = {
  installHeading: string;
  installCopy: string;
  primaryActionText: string;
  secondaryActionText: string;
};

const emptyContent: SplashContent = {
  installHeading: '',
  installCopy: '',
  primaryActionText: '',
  secondaryActionText: ''
};

const SplashConfigAdmin = (): JSX.Element => {
  const { tenant } = usePortalContext();
  const sessionTenantId = String(tenant?.tenantId ?? '').trim().toLowerCase();
  const hasAccess = hasElevatedAccess(tenant?.roles, PORTAL_ROLES.marketingAdmin);
  const [deployableTenants, setDeployableTenants] = React.useState<AdminTenantOption[]>([]);
  const [selectedTenantId, setSelectedTenantId] = React.useState('');
  const [content, setContent] = React.useState<SplashContent>(emptyContent);
  const [loading, setLoading] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [savedMessage, setSavedMessage] = React.useState<string | null>(null);

  const load = React.useCallback(async (): Promise<void> => {
    if (!hasAccess) {
      return;
    }
    if (!selectedTenantId) {
      return;
    }
    setLoading(true);
    setError(null);
    setSavedMessage(null);
    try {
      const response = await fetch(`/api/admin/splash/enterprise-uninstalled?tenantId=${encodeURIComponent(selectedTenantId)}`);
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload?.message || payload?.error || `Failed to load config (${response.status})`);
      }
      const next = (payload?.content ?? {}) as Record<string, unknown>;
      setContent({
        installHeading: typeof next.installHeading === 'string' ? next.installHeading : '',
        installCopy: typeof next.installCopy === 'string' ? next.installCopy : '',
        primaryActionText: typeof next.primaryActionText === 'string' ? next.primaryActionText : '',
        secondaryActionText: typeof next.secondaryActionText === 'string' ? next.secondaryActionText : ''
      });
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, [hasAccess, selectedTenantId]);

  React.useEffect(() => {
    const loadTenants = async (): Promise<void> => {
      if (!hasAccess) {
        return;
      }
      try {
        const response = await fetch('/api/admin/spfx-releases/deployable-tenants');
        const payload = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(payload?.message || `Failed to load tenants (${response.status})`);
        }
        const tenants = Array.isArray(payload?.tenants) ? payload.tenants as AdminTenantOption[] : [];
        setDeployableTenants(tenants);
        setSelectedTenantId(payload?.defaultTenantId || tenants[0]?.tenantId || sessionTenantId);
      } catch (err) {
        setError((err as Error).message);
      }
    };
    void loadTenants();
  }, [hasAccess, sessionTenantId]);

  React.useEffect(() => {
    void load();
  }, [load]);

  const save = async (): Promise<void> => {
    setSaving(true);
    setError(null);
    setSavedMessage(null);
    try {
      const response = await fetch('/api/admin/splash/enterprise-uninstalled', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tenantId: selectedTenantId, content })
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload?.message || payload?.error || `Failed to save config (${response.status})`);
      }
      setSavedMessage('Enterprise splash content saved.');
      await load();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSaving(false);
    }
  };

  if (!hasAccess) {
    return (
      <div className="card">
        <h1 className="section-title">Splash Content Admin</h1>
        <p className="muted">You do not have permission to manage splash content.</p>
      </div>
    );
  }

  return (
    <div>
      <h1 className="section-title">Splash Content Admin</h1>
      <p className="muted">
        Configure enterprise-specific content for the uninstalled splash shown to licensed Enterprise tenants.
      </p>

      <div className="card" style={{ marginTop: '12px' }}>
        <h3>Enterprise Uninstalled Splash</h3>
        <div style={{ marginTop: '10px', marginBottom: '12px' }}>
          <AdminTenantSelector
            value={selectedTenantId}
            options={deployableTenants}
            onChange={setSelectedTenantId}
            disabled={saving || loading}
            helperText="Splash content is tenant-scoped. Edit one covered tenant at a time."
          />
        </div>

        <div style={{ display: 'grid', gap: '10px' }}>
          <label>
            <div className="muted" style={{ marginBottom: '4px' }}>Install heading</div>
            <input
              className="input"
              value={content.installHeading}
              onChange={(event) => setContent((prev) => ({ ...prev, installHeading: event.target.value }))}
              maxLength={180}
            />
          </label>

          <label>
            <div className="muted" style={{ marginBottom: '4px' }}>Install copy</div>
            <textarea
              className="input"
              value={content.installCopy}
              onChange={(event) => setContent((prev) => ({ ...prev, installCopy: event.target.value }))}
              rows={4}
              maxLength={1800}
            />
          </label>

          <div className="grid columns-2">
            <label>
              <div className="muted" style={{ marginBottom: '4px' }}>Primary action text</div>
              <input
                className="input"
                value={content.primaryActionText}
                onChange={(event) => setContent((prev) => ({ ...prev, primaryActionText: event.target.value }))}
                maxLength={80}
              />
            </label>

            <label>
              <div className="muted" style={{ marginBottom: '4px' }}>Secondary action text</div>
              <input
                className="input"
                value={content.secondaryActionText}
                onChange={(event) => setContent((prev) => ({ ...prev, secondaryActionText: event.target.value }))}
                maxLength={80}
              />
            </label>
          </div>

          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <button className="btn" type="button" onClick={() => { void save(); }} disabled={saving || loading}>
              {saving ? 'Saving...' : 'Save splash content'}
            </button>
            <button className="btn secondary" type="button" onClick={() => { void load(); }} disabled={saving || loading}>
              Reload
            </button>
          </div>

          {loading && <span className="badge-subtle">Loading splash content...</span>}
          {error && <span className="pill warning">{error}</span>}
          {savedMessage && <span className="pill success">{savedMessage}</span>}
        </div>
      </div>
    </div>
  );
};

export default SplashConfigAdmin;
